import { Button, ConfigProvider, Form, InputNumber, message, Modal } from "antd";
import dayjs from "dayjs";
import { useState, useRef, useCallback } from "react";
import * as XLSX from 'xlsx';
import TableUploadExcel from "./table";
import { PostOrders } from "../../../../API/PostData";
import { FileExcelFilled, FileExcelOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";
import { AuthData } from "../../../../Components/Global";

const ImportExcelOrder = ({ onImportData }) => {
    const Auth = AuthData();
    const AccountId = Auth && Auth.AccountId;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [dataExcel, setDataExcel] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const fileInputRef = useRef(null);

    const [form] = Form.useForm();
    const [fileName, setFileName] = useState("");
    const [fileValue, setFileValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    // Memoize the data transformation logic
    const transformOrderData = useCallback((data) => {
        const filteredData = data.filter(item => item.QuotationNumber);

        const GroupByQuotationNumber = filteredData.reduce((acc, item) => {
            const key = item.QuotationNumber;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});

        return Object.keys(GroupByQuotationNumber).map((QuotationNumber) => {
            const items = GroupByQuotationNumber[QuotationNumber];
            const itemsOrder = items[0];

            // Find max TAT across all items in the group
            const maxTAT = Math.max(...items.map(item => item.TAT || 0));

            // Group samples by SampleNumber within this quotation
            const samplesMap = {};
            items.forEach(item => {
                if (!samplesMap[item.SampleNumber]) {
                    samplesMap[item.SampleNumber] = {
                        sample: item,
                        parameters: []
                    };
                }

                // Add parameter for this item
                samplesMap[item.SampleNumber].parameters.push({
                    ParameterName: item.Parameter,
                    Methode: item.ReferenceMethod,
                    SubcontStatus: Boolean(item.LabSubcont),
                    ReportNumber: item.ReportNumber,
                    PackageQuantity: item.PackageQuantity,
                    QuantityperPackage: item.QuantityperPackage,
                    LabSubcont: item.LabSubcont,
                    TAT: item.TAT,
                    DueDate: item.DueDate,
                    Amount: parseInt((item.Amount || '').replace(/[^\d]/g, '') || '0', 10),
                });
            });

            // Convert samples map to array format
            const sampleArray = Object.keys(samplesMap).map((SampleNumber) => {
                const sampleData = samplesMap[SampleNumber];

                const maxDueDate = Math.max(...sampleData.parameters.map(param =>
                    param.DueDate ? new Date(param.DueDate).getTime() : 0
                ) || Date.now());

                return {
                    RFNumber: sampleData.sample.RFNumber,
                    SampleNumber: SampleNumber,
                    SampleName: sampleData.sample.SampleName,
                    ServiceType: sampleData.sample.ServiceType,
                    ReceivedDate: dayjs(sampleData.sample.ReceivingDate).format('YYYY-MM-DD'),
                    COADueDate: new Date(maxDueDate).toISOString(),
                    Description: "",
                    Parameter: sampleData.parameters
                };
            });

            // const totalParameters = sampleArray.reduce((count, sample) => count + sample.Parameter.length, 0);
            // console.log(totalParameters);

            // console.log(sampleArray.length);


            return {
                Order: {
                    AccountId: AccountId,
                    OrderDate: dayjs().format('YYYY-MM-DD'),
                    OrderQuotation: QuotationNumber,
                    CustomerId: itemsOrder.ClientCode,
                    SalesCode: itemsOrder.SalesCode,
                    Department: itemsOrder.Department,
                    MaxTAT: maxTAT.toString(),
                    ReviewedBy: AccountId,
                    Description: "",
                    IsStatus: false,
                },
                Sales: itemsOrder.SalesCode ? [
                    {
                        AccountId: AccountId,
                        SalesCode: itemsOrder.SalesCode,
                        SalesName: itemsOrder.SalesName,
                        SalesNumber: itemsOrder.ExistingServices
                    }
                ] : [],
                Customer: [
                    {
                        AccountId: AccountId,
                        CustomerName: itemsOrder.Client
                    }
                ],
                Sample: sampleArray
            };
        });
    }, [AccountId]);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            const Order = transformOrderData(dataSource);
            const res = await PostOrders(Order);
            console.log(Order);

            if (res.status === 201) {
                message.success(res.data.message);
                onImportData(true);
                handleCancel();
            }
        } catch (error) {
            console.error("Error submitting order:", error);
            message.error("Failed to submit order");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setIsModalOpen(false);
        setDataExcel([]);
        setDataSource([]);
        setFileName("");
        setFileValue(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Optimized file processing with web workers or chunking
    const processExcelData = (data, fromRow, toRow) => {
        // Filter based on range if provided
        let filteredData = data;
        if (fromRow && toRow) {
            filteredData = data.slice(fromRow - 1, toRow);
        }

        return filteredData.map(row => {
            const newRow = {};
            Object.keys(row).forEach(key => {
                const trimmedKey = key.trim();
                let newKey = trimmedKey.replace(/\s+/g, '');

                // console.log(trimmedKey);

                // Key mapping
                if (trimmedKey === "Technical/Reference Method") {
                    newKey = "ReferenceMethod";
                }
                if (trimmedKey.startsWith("Technical")) {
                    newKey = "ReferenceMethod";
                }
                if (trimmedKey === "New/Existing Services") {
                    newKey = "ExistingServices";
                }


                const value = row[key];

                // Skip processing if value is undefined or null
                if (value === undefined || value === null) {
                    newRow[newKey] = value;
                    return;
                }

                // Time format processing
                if (typeof value === 'string' && /\d{1,2}:\d{2}:\d{2}\s*(AM|PM)/i.test(value)) {
                    const timeParts = value.match(/(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)/i);
                    if (timeParts) {
                        let hours = parseInt(timeParts[1]);
                        const minutes = timeParts[2];
                        const seconds = timeParts[3];
                        const meridiem = timeParts[4].toUpperCase();

                        if (meridiem === 'PM' && hours < 12) hours += 12;
                        if (meridiem === 'AM' && hours === 12) hours = 0;

                        newRow[newKey] = `${hours.toString().padStart(2, '0')}:${minutes}:${seconds}`;
                    } else {
                        newRow[newKey] = value;
                    }
                }
                // Date processing
                else if (typeof value === 'number' && newKey.toLowerCase().includes('date')) {
                    try {
                        const excelDate = XLSX.SSF.parse_date_code(value);
                        newRow[newKey] = dayjs(new Date(excelDate.y, excelDate.m - 1, excelDate.d)).format('YYYY-MM-DD');
                    } catch {
                        newRow[newKey] = value;
                    }
                }
                // Already Date object
                else if (value instanceof Date) {
                    newRow[newKey] = dayjs(value).format('YYYY-MM-DD HH:mm:ss');
                }
                // Default case
                else {
                    newRow[newKey] = value;
                }
            });
            return newRow;
        });
    };

    const handleFileUpload = useCallback((e, fromRow, toRow) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, {
                    type: 'binary',
                    cellDates: true,
                    dense: true
                });

                let targetSheet;

                if (wb.SheetNames.length > 1) {
                    const targetSheetName = 'Login';
                    targetSheet = wb.Sheets[targetSheetName];

                    if (!targetSheet) {
                        throw new Error("Sheet 'Login' tidak ditemukan di file Excel");
                    }
                } else {
                    // Jika hanya satu sheet, langsung ambil sheet pertama
                    const firstSheetName = wb.SheetNames[0];
                    targetSheet = wb.Sheets[firstSheetName];
                }

                // Convert sheet ke JSON
                const rawData = XLSX.utils.sheet_to_json(targetSheet, {
                    range: 1, // Skip header row
                    raw: false,
                    dateNF: 'yyyy-mm-dd hh:mm:ss'
                });

                // Process data
                setTimeout(() => {
                    const processedData = processExcelData(rawData, fromRow, toRow);
                    setDataExcel(processedData);
                    setLoading(false);
                }, 0);

            } catch (error) {
                console.error("Error processing file:", error);
                message.error(`Failed to process file: ${error.message}`);
                setLoading(false);
            }
        };

        reader.onerror = () => {
            console.error("FileReader error");
            message.error("Error reading file");
            setLoading(false);
        };

        reader.readAsBinaryString(file);
    }, []);



    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFileName(file.name);
            setFileValue(file);
        }
    };

    const handleFind = (values) => {
        if (!fileValue) {
            message.warning("Please select a file first");
            return;
        }

        setLoading(true);
        const mockEvent = { target: { files: [fileValue] } };
        handleFileUpload(mockEvent, values.From, values.To);
    };

    return (
        <>
            <ConfigProvider
                theme={{
                    components: {
                        Button: {
                            colorPrimary: '#21b6d7',
                            colorPrimaryHover: '#1aa0bd',
                            colorPrimaryActive: '#178ca7',
                            colorTextLightSolid: '#fff',
                            lineWidth: 0,
                        },
                    },
                }}
            >
                <Button
                    onClick={showModal}
                    type="primary"
                    icon={<FileExcelOutlined />}
                >
                    Import
                </Button>
            </ConfigProvider>

            <Modal
                style={{
                    maxWidth: "90%",
                    minWidth: "80%",
                }}
                centered
                title="IMPORT FROM EXCEL"
                open={isModalOpen}
                footer={null}
                closable={false}
            >
                <div className="flex flex-col w-full gap-4">
                    <div className="h-full">
                        <input
                            type="file"
                            onChange={handleFileChange}
                            accept=".xlsx, .xls"
                            className="hidden"
                            ref={fileInputRef}
                        />
                        <div className="flex flex-col items-center justify-center border p-4 gap-4 w-full h-40 rounded-lg">
                            {fileName ? (
                                <div className="flex items-center justify-center text-primary gap-1">
                                    <FileExcelFilled />
                                    <p className="text-sm font-bold mb-1">{fileName}</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <FileExcelOutlined className="text-5xl text-gray-700" />
                                    <p>Select your file .xlsx or .xls</p>
                                </div>
                            )}
                            <Button icon={<UploadOutlined />} onClick={handleButtonClick}>
                                Upload File
                            </Button>
                        </div>
                    </div>

                    <Form layout={"vertical"} form={form} onFinish={handleFind}>
                        <div className="grid grid-cols-2 gap-4">
                            <Form.Item label="From Row" name="From">
                                <InputNumber min={1} placeholder="Input From Row" className="w-full" />
                            </Form.Item>
                            <Form.Item label="To Row" name="To">
                                <InputNumber min={1} placeholder="Input To Row" className="w-full" />
                            </Form.Item>
                        </div>
                        <div className="flex justify-end">
                            <Form.Item>
                                <Button
                                    loading={loading}
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SearchOutlined />}
                                    disabled={!fileValue}
                                >
                                    Find
                                </Button>
                            </Form.Item>
                        </div>
                    </Form>

                    <TableUploadExcel
                        dataSource={dataExcel}
                        onData={setDataSource}
                    />
                </div>

                <div className="flex justify-end gap-2 border-t pt-4 mt-4">
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={isLoading}
                        disabled={dataSource.length === 0}
                    >
                        Submit
                    </Button>
                    <Button onClick={handleCancel} disabled={isLoading || loading}>Cancel</Button>
                </div>
            </Modal>
        </>
    )
}

export default ImportExcelOrder;